import {
  Component,
  EventEmitter,
  Output,
  inject,
  signal,
  effect,
  OnInit,
  Input,
  ViewChild,
  ElementRef,
} from '@angular/core';

import {
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { debounceTime, distinctUntilChanged, switchMap, of, lastValueFrom, take, takeUntil } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '../../../environments/environment';
import { Store } from '@ngrx/store';
import { accountFeature } from '../../store/account.store';
import { getGooglePlaceIds, GetGooglePlaceIdsVariables } from '@dataconnect/account';
import { LOCATION_LABEL } from '../../../../globalTypes/configs';

export interface AddressAutocompleteReturn {
  id: string,
  displayName: string,
  formattedAddress: string,
  name:  string,
  photo:  string,
  photos: {name: string, heightPx: number, widthPx: number, googleMapsUri: string}[],
  latitude: number,
  longitude: number,
  streetNumber: string,
  route: string,
  city:string,
  postalCode: string,
  state: string,
  country: string,
  types: string[],
}

interface PlaceSuggestion {
  id: string;
  displayName: {text:string, languageCode: string};
  formattedAddress: string;
  name: string;
  alreadyTaken?: boolean; // for indicating if this place is already associated with an existing location in our system
}

interface PlacesPhoto {
  name: string, // places/ChIJN1t_tDeuEmsRUsoyG83so7o/photos/AUGGfZsDpXlQ7QaqVlNfgDgiD2P5Vn-tTXXX",
  widthPx: number,
  heightPx: number,
}

@Component({
  selector: 'msh-address-autocomplete',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatInputModule
],
  template: `
    <div class="w-full max-w-lg mx-auto flex flex-col gap-4">
      @if (selectedImageUrl()) {
        <span class=" first-letter:capitalize">{{addressType}} {{LOCATION_LABEL}} selected</span>
        <div class="w-full overflow-hidden rounded-lg shadow border border-copy/50">
          <img
            [src]="selectedImageUrl()"
            alt="Location image"
            class="w-full h-64 object-cover"
          />
        </div>
        <div class="null mt-0 text-xs">Due to Google's caching restrictions, we can't save this image to your {{LOCATION_LABEL}}. Please upload your own {{LOCATION_LABEL}} image. <br />This can be done later if needed.</div>
        <div class=" text-center text-xs cursor-pointer mb-3 font-light hover:text-orange-500" (click)="startOver()">...select a different {{LOCATION_LABEL}}</div>

      } @else {
        <!-- Address Input -->
        @if(!defaultSearchQuery){
          Search for your {{addressType}} {{LOCATION_LABEL}}:
          <input
            type="text"
            matInput
            [formControl]="addressControl"
            [matAutocomplete]="auto"
            #addressInput
            placeholder="Start typing your address..."
          />
          <mat-autocomplete
            #auto="matAutocomplete"
            [displayWith]="displayFn"
            (optionSelected)="onSelect($event.option.value)">
            @for (place of suggestions(); track place.id) {
              <mat-option [value]="place">
                <div class="text-sm">
                  <div>{{ place.displayName || place.formattedAddress }}</div>
                </div>
              </mat-option>
            }
            @if (loading()) {
              <mat-option disabled>
                <div class="flex justify-center items-center gap-2">
                  <mat-spinner diameter="20"></mat-spinner>
                  <span>Loading...</span>
                </div>
              </mat-option>
            }
          </mat-autocomplete>
        } @else {
          @if(!selectedImageUrl()){
            Select your {{addressType}} location
          }
          @for (place of suggestions(); track place.id) {
            @if(!selectionMade){
              <div (click)="onSelect(place)" [class]="' border-copy border rounded p-2 mt-2  hover:bg-orange-400/50 cursor-pointer'">
                <div class="text-sm ">
                  @if(place.alreadyTaken){
                    <div class="text-xs font-bold opacity-50 text-center uppercase mb-2 ">Existing Mobile Storehouse Member</div>
                  }
                  <div>{{ place.displayName || place.formattedAddress }}</div>
                </div>
              </div>
            }
          }
          <div class=" text-center text-xs cursor-pointer mb-3 font-light hover:text-orange-500" (click)="cancelDefaultSearch()">...can't find my address</div>
        }
      }
    </div>
  `,
})
export class AddressAutocompleteComponent implements OnInit {
  private apiKey = environment.google.places;
  private http = inject(HttpClient);
  @Input() addressType: 'physical' | 'shipping' = 'physical';
  fields =  'id,name,displayName,formattedAddress,location,photos,addressComponents,types';
  @Input() defaultSearchQuery = '';
  @ViewChild('addressInput', { read: ElementRef }) addressInput!: ElementRef<HTMLInputElement>;
  @Input() includedPrimaryTypes: string[] = []; // ['route','street_view']
  @Input() defaultIncludedPrimaryTypes = ['restaurant', 'food', 'point_of_interest', 'establishment','fast_food_restaurant'];
  @Output() placeSelected: EventEmitter<AddressAutocompleteReturn> = new EventEmitter<AddressAutocompleteReturn>();

  LOCATION_LABEL = LOCATION_LABEL;
  addressControl = new FormControl('');
  suggestions = signal<PlaceSuggestion[]>([]);
  loading = signal(false);
  selectedImageUrl = signal<string | null>(null);
  selectionMade = false;

  constructor(
    private store: Store,
  ) {

    effect(() => {
      this.addressControl.valueChanges
        ?.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((query) => {
            if (typeof query !== 'string' || !query.trim()) return of([]);
            return this.fetchPredictions(query);
          })
        )
        .subscribe( (results) => {
          console.log({results})
          this.suggestions.set(results as PlaceSuggestion[])});
    });
  }

  async ngOnInit(): Promise<void> {
    if(this.defaultSearchQuery !== ''){
      const results = await this.fetchPredictions(this.defaultSearchQuery);
      this.suggestions.set(results as PlaceSuggestion[])
    }
  }

  cancelDefaultSearch(){
    this.defaultSearchQuery = '';
    this.suggestions.set([]);
    setTimeout(() => {
      console.log({ thisAddressInput: this.addressInput})
      if(this.addressInput){
        this.addressInput.nativeElement.focus();
      }
    }, 500)
  }

  startOver(){
    this.selectionMade = false;
    this.selectedImageUrl.set(null);
    this.suggestions.set([]);
    this.addressControl.setValue('');
    setTimeout(() => {
      if(this.addressInput){
        this.addressInput.nativeElement.focus();
      }
    }, 500)
  }

  /** Fetch predictions from Google Places (New) API */
  private async fetchPredictions(query: string) {
    const url = `https://places.googleapis.com/v1/places:autocomplete`;
    const body = {
      // textQuery: 
      input: query,
      languageCode: 'en',
      includedPrimaryTypes: this.includedPrimaryTypes.length ? this.includedPrimaryTypes : this.defaultIncludedPrimaryTypes,
      // maxResultCount: 5,
    };

    const results = await lastValueFrom(this.http.post<any>(url, body, {
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': this.apiKey,
      }
    }).pipe(
      take(1),
      switchMap((data) => of(data.suggestions?.map((p: any) => ({
          id: p.placePrediction.placeId,
          displayName: p.placePrediction.text?.text,
          name: p.placePrediction.name,
          formattedAddress:
            p.placePrediction.structuredFormat?.mainText?.text || '',
        })) || []))
    ));
    console.log({results})
    const placeIdsInResult: string[] = results.map((r:any) => r.id);
    const getPlaceIdsVariables: GetGooglePlaceIdsVariables = {googlePlaceIds: placeIdsInResult}
    const takenPlacesRef = await getGooglePlaceIds(getPlaceIdsVariables);
    const unfilteredPlaceIds = takenPlacesRef.data.addresses.map((ap:any) => ap.googlePlaceId);
    const placeIds: string[] = unfilteredPlaceIds.filter(a=>!!a);
    if(placeIds.some((id:string) => placeIdsInResult.includes(id))){
      console.warn("Some results are already associated with existing locations. Consider implementing a way to indicate this in the UI and prevent selection, or remove them from the results entirely.")
    }
    return results.map((place: any) => ({
      ...place,
      alreadyTaken: placeIds.includes(place.id)
    }));
  }


  /** Display the formatted address in the input box */
  displayFn(place: any): string {
    return place?.formattedAddress || place?.displayName?.text || '';
  }

  /** When user selects an address from autocomplete */
  onSelect(place: any) {

    const url = `https://places.googleapis.com/v1/places/${place.id}?fields=${this.fields}`;
    this.selectionMade = true;
    this.http.get<any>(url, {
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': this.apiKey,
      }
    }).subscribe( async (details) => {
      console.log({details})
      const structured: AddressAutocompleteReturn = this.mapPlaceToStructuredObject(details);
      
      if(this.addressType === 'physical'){  
        if (structured.latitude && structured.longitude) {
          const imageUrl = await this.getStaticImageWithFallback(
            structured.photos,
            structured.latitude,
            structured.longitude
          );
          this.selectedImageUrl.set(imageUrl);
          structured.photo = imageUrl
        }
      }
      console.log({structured})
      this.placeSelected.emit(structured);
      this.addressControl.setValue(structured.formattedAddress, {
        emitEvent: false,
      });

    });
  }

  /** Map Google response to a structured object */

  /** Try to fetch a Street View image; fallback to static map if unavailable */
  private async getStaticImageWithFallback(photos:PlacesPhoto[] | null, lat: number, lng: number): Promise<string> {
    if(photos && photos.length > 0){
      const photoDataUrl = `https://places.googleapis.com/v1/${photos[0].name}/media?maxHeightPx=400&maxWidthPx=400&key=${this.apiKey}`;
      return photoDataUrl;
    }
    
    const streetViewUrl = `https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${lat},${lng}&key=${this.apiKey}`;
    const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=600x400&markers=color:red|${lat},${lng}&key=${this.apiKey}`;

    try {
      const res = await fetch(streetViewUrl, { method: 'HEAD' });
      // Street View returns 404 if no image is available
      if (res.ok) return streetViewUrl;
      return staticMapUrl;
    } catch {
      return staticMapUrl;
    }
  }

  private mapPlaceToStructuredObject(place: any): AddressAutocompleteReturn {
    const components = place.addressComponents || [];
    console.log({components})
    const get = (type: string) =>
      components.find((c: any) => c.types?.includes(type))?.longText || '';

    return {
      id: place.id,
      displayName: place.displayName?.text || '', // business name or place name
      formattedAddress: place.formattedAddress,
      name: place.name,
      photo: '',
      photos: place.photos,
      latitude: place.location?.latitude,
      longitude: place.location?.longitude,
      streetNumber: get('street_number'),
      route: get('route'),
      city:
        get('locality') ||
        get('postal_town') ||
        get('sublocality') ||
        get('administrative_area_level_2'),
      state: get('administrative_area_level_1'),
      postalCode: get('postal_code'),
      country: get('country'),
      types: place.types || [],
    };
  }
}
